import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Redirect, useHistory } from "react-router-dom";
import format from "date-fns/format";
import { getCountryCallingCode } from "libphonenumber-js";
import * as Yup from "yup";

// Material
import { makeStyles } from "@material-ui/core/styles";

// Actions
import { toggleSnack } from "src/state/snackbar/actions";
import { createCase } from "src/state/cases/actions";

// Config
import { snackbarTypes } from "src/config/snackbar";
import { getRoute, urls } from "src/config/routes";
import { userTypes } from "src/config/values";
import { useAppInsights } from "src/utils/telemetry/AppInsights";
import validatePhoneNumberWithCountry from "src/utils/validatePhoneNumber";

// Hooks
import useQuery from "src/hooks/useQuery";
import useTranslate from "src/hooks/useTranslate";

// Components
import Disclaimer from "src/components/Disclaimer";
import FormikHandler from "src/components/FormikHandler";
import LoadingSection from "src/components/LoadingSection";
import SectionWrapper from "src/components/SectionWrapper";
import Text from "src/components/Text";
import VictimFormContent from "src/components/CreateVictimForm/VictimFormContent";
import VictimFormHandler from "src/components/CreateVictimForm/VictimFormHandler";
import WizardStepCard from "src/components/WizardStepCard";
import infoIcon from "src/assets/icons/info_icon.svg";

const today = new Date();

const useStyles = makeStyles(theme => ({
  disclaimerWrapper: {
    backgroundColor: "#E5F8FF",
    padding: theme.spacing(2, 3),
    gap: 18,
    display: "flex",
    flexDirection: "row",
    alignItems: "center"
  }
}));

const validationDefault = texts =>
  Yup.object().shape({
    givenName: Yup.string().required(texts.required),
    familyName: Yup.string().required(texts.required),
    dateOfBirth: Yup.date().nullable().required(texts.required),
    number: Yup.string()
      .matches(/^[0-9]*$/, { message: texts.phoneNumber.mustBe })
      .min(4, texts.phoneNumber.tooShort)
      .test(
        "valid-phone",
        texts.phoneNumber.invalid,
        function validatePhoneNumber(value) {
          return validatePhoneNumberWithCountry(value, this.parent.country);
        }
      )
      .required(texts.required)
  });

const validationWitness = texts =>
  Yup.object().shape({
    givenName: Yup.string(),
    familyName: Yup.string(),
    dateOfBirth: Yup.date().nullable(),
    number: Yup.string()
      .matches(/^[0-9]*$/, { message: texts.phoneNumber.mustBe })
      .min(4, texts.phoneNumber.tooShort)
      .test(
        "valid-phone",
        texts.phoneNumber.invalid,
        function validatePhoneNumber(value) {
          return validatePhoneNumberWithCountry(
            value,
            this.parent.country,
            true
          );
        }
      )
  });

const victimInitialValues = {
  givenName: "",
  familyName: "",
  dateOfBirth: null,
  dateTimeOfExamination: today,
  country: "GB",
  number: ""
};

// placeOfExamination only ever depends on geolocation - it is unrelated to
// the incident location gathered later in the wizard (WhereStep's own
// location/coord, which only feeds incident.location).
const resolveExaminationPlace = () =>
  new Promise(resolve => {
    const withDefaults = () =>
      resolve({ coordinate: { latitude: 0, longitude: 0 }, place: "none" });

    if (!navigator?.geolocation?.getCurrentPosition) {
      withDefaults();
      return;
    }

    const HARD_TIMEOUT_MS = 60000;
    const hardTimer = setTimeout(withDefaults, HARD_TIMEOUT_MS);

    navigator.geolocation.getCurrentPosition(
      location => {
        clearTimeout(hardTimer);
        resolve({
          coordinate: {
            latitude: location.coords.latitude,
            longitude: location.coords.longitude
          },
          place: `${location.coords.latitude}, ${location.coords.longitude}`
        });
      },
      () => {
        clearTimeout(hardTimer);
        withDefaults();
      }
    );
  });

const CreateCasePage = () => {
  const classes = useStyles();
  const query = useQuery();
  const [userCapacityState, setUserCapacity] = useState(
    () => query.get("userCapacity") || null
  );
  const [coord, setCoord] = useState(null);
  const { createError, isCreating } = useSelector(s => s.cases);
  const { authState } = useSelector(s => s.user);
  const { trackException } = useAppInsights();
  const dispatch = useDispatch();
  const history = useHistory();
  const getText = useTranslate();
  const schemaTexts = getText("common.validationSchema");

  const isWitness = userCapacityState === userTypes.Witness;
  const isIntel = userCapacityState === "intelligence";

  const victimValidationSchema =
    isWitness || isIntel
      ? validationWitness(schemaTexts)
      : validationDefault(schemaTexts);

  const handleVictimSubmit = async (values, { setSubmitting }) => {
    try {
      const { coordinate, place } = await resolveExaminationPlace();
      const {
        country,
        dateOfBirth,
        dateTimeOfExamination,
        familyName,
        givenName,
        number
      } = values;

      const referenceNumber = query.get("ref");
      const organizationId = query.get("orgId");
      const organizationName = query.get("orgName");

      const body = {
        patient: {
          givenName,
          familyName,
          dateOfBirth:
            dateOfBirth && format(new Date(dateOfBirth), "yyyy-MM-dd"),
          phoneNumber: number
            ? {
                callingCountryCode: `+${getCountryCallingCode(country)}`,
                nationalNumber: number
              }
            : null
        },
        placeOfExamination: { name: place, coordinate },
        dateTimeOfExamination: format(
          dateTimeOfExamination,
          "yyyy-MM-dd'T'HH:mm:ss"
        ),
        userCapacity: isIntel ? userTypes.Witness : userCapacityState,
        isIntel
      };

      if (referenceNumber) {
        if (organizationId) {
          body.referenceNumber = referenceNumber;
          body.organizationId = organizationId;
        } else if (organizationName) {
          body.referenceNumber = referenceNumber;
          body.organizationName = organizationName;
        }
      }

      dispatch(
        createCase({
          body,
          state: values,
          onSuccess: caseId =>
            history.push(getRoute(urls.caseDetail, { caseId })),
          onFail: () => {
            window.scrollTo(0, 0);
            setSubmitting(false);
            trackException("Failed to create a new case");
          }
        })
      );
    } catch (error) {
      dispatch(toggleSnack({ type: snackbarTypes.ERROR }));
      setSubmitting(false);
      trackException(error);
    }
  };

  if (!userCapacityState && !authState?.data)
    return <Redirect to={urls.cases} />;

  return (
    <SectionWrapper isNew hideSidebar userCapacity={userCapacityState}>
      {createError && (
        <Disclaimer color="secondary" text="common.errors.request" />
      )}
      {isCreating ? (
        <LoadingSection normalHeight />
      ) : (
        <FormikHandler
          formik={{
            initialValues: victimInitialValues,
            onSubmit: handleVictimSubmit,
            validationSchema: victimValidationSchema
          }}
          render={({ values, errors, setFieldValue, isSubmitting }) => (
            <WizardStepCard
              banner={
                (isWitness || isIntel) && (
                  <div className={classes.disclaimerWrapper}>
                    <img src={infoIcon} alt="" />
                    <Text
                      text="createCasePage.optionalTextLabel"
                      type="contentLabel2"
                    />
                  </div>
                )
              }
              hideProgress
              hideSkip
              isSubmitting={isSubmitting}
              onBack={history.goBack}
              step={1}
              title="caseDetailPage.victimInfoCard.title"
            >
              <VictimFormHandler
                setCoord={setCoord}
                setFieldValue={setFieldValue}
                setUserCapacity={setUserCapacity}
                userCapacity={userCapacityState}
              >
                <VictimFormContent
                  coord={coord}
                  errors={errors}
                  values={values}
                />
              </VictimFormHandler>
            </WizardStepCard>
          )}
        />
      )}
    </SectionWrapper>
  );
};

export default CreateCasePage;
